// Typed wrappers for the four /v1/merchants/:id/api-keys endpoints.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
//
// Secret discipline (CLAUDE.md rule 2, T-4-12): `secret` is a field ONLY on
// MintApiKeyResult and RotateApiKeyResult — the two one-time response types.
// The list ApiKey interface has no `secret` field. This ensures the type system
// prevents a secret from being read off a list response or persisted in a typed
// store. The backend returns the secret exactly once; it is never re-fetchable.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Enumerations / string literal unions
// ---------------------------------------------------------------------------

/**
 * Scope values a minted API key can carry (D-02).
 *
 * - `payouts:read` / `payouts:write` — refresh + initiate payouts.
 * - `balances:read` — reserved for the live balance endpoint.
 * - `accounts:read` — GET /v1/merchants/:id (profile + embedded bank rails)
 *   , GET /v1/merchants/:id/bank-accounts and GET /v1/merchants/:id/bank-rails
 *   (ids + status only). See payouts-backend#5 — added
 *   so M2M callers can bootstrap (which `bank_account_id` to use, what's
 *   my merchant slug/status) without an out-of-band call.
 */
export type ApiKeyScope =
  | "payouts:read"
  | "payouts:write"
  | "balances:read"
  | "accounts:read";

/**
 * Status of an API key, derived by the backend from timestamp columns:
 *   disabled_at != null  → "revoked"
 *   retired_at  != null  → "retired"
 *   both null            → "active"
 */
export type ApiKeyStatus = "active" | "revoked" | "retired";

// ---------------------------------------------------------------------------
// Response / payload interfaces
// ---------------------------------------------------------------------------

/**
 * ApiKey — the non-secret API-key representation returned by the backend.
 * CRITICALLY: no `secret` field. The secret is returned ONCE in
 * MintApiKeyResult / RotateApiKeyResult and is never re-fetchable.
 */
export interface ApiKey {
  id: number;
  merchant_id: number;
  name: string;
  prefix: string;
  scopes: ApiKeyScope[];
  status: ApiKeyStatus;
  last_used_at: string | null;
  created_by: number | null;
  created_at: string;
  disabled_at: string | null;
  retired_at: string | null;
}

/**
 * MintApiKeyPayload — POST /v1/merchants/:id/api-keys request body.
 * No `env` field (D-02 — live only, no sandbox toggle).
 */
export interface MintApiKeyPayload {
  name: string;
  scopes: ApiKeyScope[];
}

/**
 * MintApiKeyResult — POST /v1/merchants/:id/api-keys response.
 * `secret` is the plaintext key returned ONCE. Store it immediately —
 * closing the RevealKey modal drops it from memory and it cannot be recovered.
 */
export interface MintApiKeyResult {
  api_key: ApiKey;
  secret: string;
}

/**
 * RotateApiKeyResult — POST /v1/merchants/:id/api-keys/:kid/rotate response.
 * `api_key` is the NEW replacement key row; the old key is immediately retired.
 * `secret` is the new plaintext key returned ONCE.
 */
export interface RotateApiKeyResult {
  api_key: ApiKey;
  secret: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * listApiKeys — GET /v1/merchants/:merchantId/api-keys
 * Returns the merchant's API keys (active, revoked, and retired). Requires
 * viewer+ membership (D-01).
 */
export async function listApiKeys(merchantId: number): Promise<ApiKey[]> {
  const resp = await apiClient.get<{ api_keys: ApiKey[] }>(
    `/v1/merchants/${merchantId}/api-keys`,
  );
  return resp.data.api_keys;
}

/**
 * mintApiKey — POST /v1/merchants/:merchantId/api-keys
 * Mints a new API key. Returns the key row and the plaintext secret (once only).
 * Requires operator+ membership (D-01).
 */
export async function mintApiKey(
  merchantId: number,
  payload: MintApiKeyPayload,
): Promise<MintApiKeyResult> {
  const resp = await apiClient.post<MintApiKeyResult>(
    `/v1/merchants/${merchantId}/api-keys`,
    payload,
  );
  return resp.data;
}

/**
 * rotateApiKey — POST /v1/merchants/:merchantId/api-keys/:keyId/rotate
 * Retires the existing key immediately and mints a replacement. Returns the
 * new key row and the new plaintext secret (once only). No request body.
 * Requires operator+ membership (D-01).
 */
export async function rotateApiKey(
  merchantId: number,
  keyId: number,
): Promise<RotateApiKeyResult> {
  const resp = await apiClient.post<RotateApiKeyResult>(
    `/v1/merchants/${merchantId}/api-keys/${keyId}/rotate`,
  );
  return resp.data;
}

/**
 * revokeApiKey — POST /v1/merchants/:merchantId/api-keys/:keyId/revoke
 * Permanently disables a key (sets disabled_at). The key remains visible in
 * the list with status "revoked" (D-03). Requires operator+ membership (D-01).
 */
export async function revokeApiKey(
  merchantId: number,
  keyId: number,
): Promise<{ ok: boolean }> {
  const resp = await apiClient.post<{ ok: boolean }>(
    `/v1/merchants/${merchantId}/api-keys/${keyId}/revoke`,
  );
  return resp.data;
}
